import type { DexPipelineStage, DexProcessingStatus } from "#graphql/supergraphTypes";

export type ChecklistStepState = "done" | "in_progress" | "failed" | "pending";
export type ChecklistStepKey = "UPLOAD" | DexPipelineStage;

export interface ChecklistStep {
  key: ChecklistStepKey;
  label: string;
  state: ChecklistStepState;
}

// Ordered pipeline. UPLOAD is implicit — the row exists only after upload — and
// is not part of the DexPipelineStage enum.
const STEP_ORDER: ChecklistStepKey[] = ["UPLOAD", "EXTRACT", "STORE", "CATALOG"];
const STAGE_ORDER: DexPipelineStage[] = ["EXTRACT", "STORE", "CATALOG"];
const STEP_LABELS: Record<ChecklistStepKey, string> = {
  UPLOAD: "Upload",
  EXTRACT: "Extract",
  STORE: "Store",
  CATALOG: "Catalog",
};

// Terminal failure statuses for the current stage. Typed as a string set (not
// DexProcessingStatus) because the classification-failure values aren't in the
// generated supergraph enum yet — once dex's DEX-17 schema is propagated and
// codegen regenerated, this can key off DexProcessingStatus directly.
const FAILURE_STATUSES = new Set<string>([
  "FAILED",
  "CLASSIFICATION_FAILED",
  "CLASSIFICATION_TIMEOUT",
  "CLASSIFICATION_UNRELIABLE",
]);

export function deriveChecklist(
  stage: DexPipelineStage,
  status: DexProcessingStatus,
): ChecklistStep[] {
  const current = STAGE_ORDER.indexOf(stage);
  return STEP_ORDER.map((key) => {
    if (key === "UPLOAD") return { key, label: STEP_LABELS[key], state: "done" };
    const idx = STAGE_ORDER.indexOf(key);
    let state: ChecklistStepState;
    if (idx < current) state = "done";
    else if (idx > current) state = "pending";
    else if (status === "DONE") state = "done";
    else if (FAILURE_STATUSES.has(status)) state = "failed";
    else state = "in_progress";
    return { key, label: STEP_LABELS[key], state };
  });
}
